import React, {useEffect, useState} from "react";
import {connect} from "react-redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import ChildRenderer from "../../../../common_components/child_renderer";
import {isInitialised} from "../../../../utils/uninitialised";
import Permissioner from "../../../../utils/permissioner";
import DataAssetsTabs from "../components/data_assets_tabs";
import {User} from "common/types/user";
import {Workflow} from "common/flowmaster/types/workflow";
import {bindActionCreators} from "redux";

import documentsFetchAction from "modules/documents/actions/documents_fetch_all";
import documentsClearAction from "modules/documents/actions/documents_clear";
import requestor from "requestor";
import {OrganisationId} from "common/types/organisation";
import {parseQuery} from "utils/uri";
import {RouterState, InjectedRouter} from "react-router/lib/Router";
import {Project, ProjectId} from "common/types/project";
import {DocumentId} from "common/types/document";
import clearLlmRuns from "modules/flowmaster/actions/llm_runs_clear";
import formatDateToLocalISOString from "routes/llm_run/utils/format_date_local_iso_string";
import {getEntityFromLocationSearch} from "routes/data_assets/util/get_entity_from_url";
import updateUrlParamsWithDispatch from "routes/data_assets/util/update_url_params_with_dispatch";

export interface Entity {
  id: number;
  name: string;
}

export interface Filters {
  from: string;
  to: string;
  user: string;
}

export type Document = Entity;

interface DataAssetsBaseContainerProps {
  user: User;
  dispatch: (args: unknown) => unknown;
  location: {
    pathname: string;
    search: string;
  };
  definitions: Workflow[];
  projects: Project[];
  params: {
    organisationId: string;
  };
  router: RouterState & InjectedRouter;
  documents: Document[];
  fetchDocuments: (
    organisationId: OrganisationId,
    projectId: ProjectId,
  ) => void;
  clearLlmRuns: () => void;
  clearDocuments: () => void;
}

const styles = {
  tabsContainer: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  loadingContainer: {
    flexGrow: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
} as const;

function readDocumentId(queryParams) {
  return queryParams.document ? parseInt(queryParams.document, 10) : undefined;
}

function readFilters(queryParams): Filters {
  const {from, to, user} = queryParams;

  if (!from) {
    const newFrom = getOneWeekAgoString();
    return {from: newFrom, to, user};
  }

  return {from, to, user};
}

const AUTO_REFRESH_STORAGE_KEY = "dataAssetAutoRefresh";

const DataAssetsBaseContainer: React.FC<DataAssetsBaseContainerProps> = props => {
  const {
    user,
    location,
    definitions,
    router,
    projects,
    fetchDocuments,
    clearDocuments,
    clearLlmRuns,
    dispatch,
  } = props;

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem(AUTO_REFRESH_STORAGE_KEY);
    return saved !== null ? JSON.parse(saved) : true;
  });

  const handleToggleAutoRefresh = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.checked;
    setAutoRefreshEnabled(newValue);
    localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, JSON.stringify(newValue));
  };

  const organisationId = parseInt(router.params.organisationId, 10);

  const queryParams = parseQuery(router.location.search);
  const [projectId, setProjectId] = useState<ProjectId | null>(
    queryParams.project ? parseInt(queryParams.project, 10) : null,
  );
  const [documentId, setDocumentId] = useState<DocumentId | undefined | null>(
    readDocumentId(queryParams),
  );

  const [filters, setFilters] = useState<Filters>(readFilters(queryParams));
  const [maxRuns, setMaxRuns] = useState<number>(
    parseInt(queryParams.max_runs || "100", 10),
  );

  const updateValues = pairs => updateUrlParamsWithDispatch(pairs, dispatch);

  useEffect(() => {
    if (projectId === null) {
      clearDocuments();
      return;
    }
    fetchDocuments(organisationId, projectId);
  }, [projectId]);

  useEffect(() => {
    updateValues({project: projectId, document: documentId});
  }, [documentId, projectId]);

  useEffect(() => {
    updateValues((filters as unknown) as Record<string, string>);
  }, [filters]);

  useEffect(() => {
    updateValues({max_runs: maxRuns.toString()});
  }, [maxRuns]);

  useEffect(() => {
    const entity = getEntityFromLocationSearch(location.search);

    const newDocumentId = entity.documentId
      ? parseInt(entity.documentId, 10)
      : null;
    const newProjectId = entity.projectId
      ? parseInt(entity.projectId, 10)
      : null;

    if (newDocumentId !== documentId) {
      setDocumentId(newDocumentId);
    }
    if (newProjectId !== projectId) {
      setProjectId(newProjectId);
    }
  }, [location.search]);

  useEffect(() => {
    setFilters(readFilters(parseQuery(router.location.search)));
  }, [location.search]);

  const shouldRenderContainer = isInitialised([user, definitions]);

  if (!shouldRenderContainer || !user.permissions) {
    return (
      <div style={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  const permissioner = new Permissioner(user);
  if (
    !(permissioner.hasPermission("view-data-assets") || permissioner.isAdmin())
  ) {
    return permissioner.getNoPermissionMessage();
  }

  const setProjectIdHandler = (projectId: ProjectId | null) => {
    setProjectId(projectId);
    setDocumentId(null);
  };

  const setDocumentIdHandler = documentId => {
    setDocumentId(documentId);
  };

  const setDatesHandler = newDates => {
    if (!newDates.from) {
      newDates.from = getOneWeekAgoString();
    }
    setFilters({...filters, ...newDates});
  };

  const setFiltersHanlder = (newFilters: Partial<Filters>) => {
    setFilters({...filters, ...newFilters});
  };

  const setMaxRunsHandler = (newMaxRuns: number) => {
    setMaxRuns(newMaxRuns);
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <div style={styles.tabsContainer}>
        <DataAssetsTabs
          {...props}
          currentPath={location.pathname}
          organisationId={organisationId}
          documents={props.documents}
          projectId={projectId}
          projects={projects}
          selectProject={setProjectIdHandler}
          documentId={documentId}
          selectDocument={setDocumentIdHandler}
          clearLlmRuns={clearLlmRuns}
          filters={filters}
          setFilters={setFiltersHanlder}
          setDates={setDatesHandler}
          maxRuns={maxRuns}
          setMaxRuns={setMaxRunsHandler}
          autoRefreshEnabled={autoRefreshEnabled}
          onAutoRefreshToggle={handleToggleAutoRefresh}
        />
      </div>
      <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
        <ChildRenderer {...props} autoRefreshEnabled={autoRefreshEnabled} />
      </div>
    </div>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        fetchDocuments: documentsFetchAction(requestor),
        clearDocuments: documentsClearAction,
        clearLlmRuns,
      },
      dispatch,
    ),
  };
}

function mapStateToProps(state) {
  return {
    user: state.user,
    definitions: state.workflows,
    projects: state.projects,
    documents: state.documents,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DataAssetsBaseContainer);

function getOneWeekAgoString() {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  oneWeekAgo.setHours(0, 0, 0, 0);
  return formatDateToLocalISOString(oneWeekAgo);
}
